import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';

import { PageContainer } from '../layout/PageContainer';
import {
  BODY_TYPE_CATEGORIES,
  TOP_N,
  adacRanking,
  adacRegistrationYears,
  averageBreakdownRate,
  averagePriceEur,
  bodyTypeLabel,
  displayName,
  tuevRanking,
  type RateEntry,
  type ReliabilityDocument,
  type ReliabilityModel,
} from '../../domain/reliability';
import { useReliability } from '../../hooks/useReliability';
import { ModelName, ReliabilitySources, ReliabilityUnavailable, ScoreChip } from './ReliabilityShared';

const euro = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

type SortKey = 'score' | 'name' | 'adac' | 'tuev' | 'price';
type SourceFilter = 'all' | 'adac' | 'tuev' | 'both';

function bestTuevRate(model: ReliabilityModel): number | null {
  const rates = Object.values(model.tuev?.byAgeClass ?? {}).map((o) => o.defectRate);
  return rates.length ? Math.min(...rates) : null;
}

/** Sort value per column; nulls always sort last regardless of direction. */
const SORT_VALUE: Record<SortKey, (m: ReliabilityModel) => number | string | null> = {
  score: (m) => m.score,
  name: (m) => displayName(m).toLowerCase(),
  adac: averageBreakdownRate,
  tuev: bestTuevRate,
  price: averagePriceEur,
};

function compareModels(a: ReliabilityModel, b: ReliabilityModel, key: SortKey, direction: 'asc' | 'desc'): number {
  const va = SORT_VALUE[key](a);
  const vb = SORT_VALUE[key](b);
  if (va === null || vb === null) {
    return va === vb ? 0 : va === null ? 1 : -1;
  }
  const result = typeof va === 'string' ? va.localeCompare(vb as string) : va - (vb as number);
  return direction === 'asc' ? result : -result;
}

function ModelDetails({ model, document }: { model: ReliabilityModel; document: ReliabilityDocument }) {
  const years = Object.entries(model.adac?.byRegistrationYear ?? {}).sort((a, b) => Number(b[0]) - Number(a[0]));
  const ageClasses = document.sources.tuev?.ageClasses ?? Object.keys(model.tuev?.byAgeClass ?? {});
  const tuevRows = ageClasses.filter((cls) => model.tuev?.byAgeClass[cls]);
  const classAverages = document.sources.tuev?.classAverages ?? {};

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ py: 2, px: 1 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          ADAC breakdowns per 1,000 cars, by first registration
        </Typography>
        {years.length ? (
          <Table size="small">
            <TableBody>
              {years.map(([year, value]) => (
                <TableRow key={year}>
                  <TableCell>{year}</TableCell>
                  <TableCell align="right">{value.toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not in the ADAC statistics.
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          TÜV defect rate by age
        </Typography>
        {tuevRows.length ? (
          <Table size="small">
            <TableBody>
              {tuevRows.map((cls) => {
                const observation = model.tuev!.byAgeClass[cls];
                return (
                  <TableRow key={cls}>
                    <TableCell>{cls} years</TableCell>
                    <TableCell align="right">
                      {observation.defectRate.toFixed(1)}%
                      {classAverages[cls] !== undefined && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}
                          (avg. {classAverages[cls].toFixed(1)}%)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {observation.rank !== null && observation.list && (
                        <Typography variant="body2" color={observation.list === 'best' ? 'success.main' : 'error.main'}>
                          #{observation.rank} {observation.list === 'best' ? 'best' : 'worst'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {document.sources.tuev?.coverage === 'partial' ? 'Not in the published TÜV rankings.' : 'Not in the TÜV-Report.'}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Score breakdown
        </Typography>
        <Typography variant="body2">ADAC: {model.scoreBreakdown.adac ?? '–'}</Typography>
        <Typography variant="body2">TÜV: {model.scoreBreakdown.tuev ?? '–'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Based on {model.observations} data point{model.observations === 1 ? '' : 's'}. Report names:{' '}
          {model.sourceNames.join(', ')}
        </Typography>
      </Box>
    </Stack>
  );
}

function ModelRow({ model, document }: { model: ReliabilityModel; document: ReliabilityDocument }) {
  const [open, setOpen] = useState(false);
  const adacRate = averageBreakdownRate(model);
  const tuevRate = bestTuevRate(model);
  const price = averagePriceEur(model);
  const bodyTypes = [...new Set(model.bodyTypes.map(bodyTypeLabel).filter(Boolean))];

  return (
    <Fragment>
      <TableRow hover sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? 'Hide details' : 'Show details'} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <ModelName model={model} variant="body2" />
          {bodyTypes.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block">
              {bodyTypes.join(', ')}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <ScoreChip score={model.score} />
        </TableCell>
        <TableCell align="right">{adacRate !== null ? adacRate.toFixed(1) : '–'}</TableCell>
        <TableCell align="right">{tuevRate !== null ? `${tuevRate.toFixed(1)}%` : '–'}</TableCell>
        <TableCell align="right">{price !== null ? euro.format(price) : '–'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <ModelDetails model={model} document={document} />
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

function AllModelsTable({ document }: { document: ReliabilityDocument }) {
  const [query, setQuery] = useState('');
  const [bodyType, setBodyType] = useState('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const category = BODY_TYPE_CATEGORIES.find((c) => c.id === bodyType);
    return document.models
      .filter((m) => !needle || [displayName(m), ...m.sourceNames].some((n) => n.toLowerCase().includes(needle)))
      .filter((m) => !category || m.bodyTypes.some((t) => category.types.includes(t)))
      .filter((m) => {
        if (source === 'adac') return m.adac !== null;
        if (source === 'tuev') return m.tuev !== null;
        if (source === 'both') return m.adac !== null && m.tuev !== null;
        return true;
      })
      .sort((a, b) => compareModels(a, b, sortKey, direction) || a.id.localeCompare(b.id));
  }, [document.models, query, bodyType, source, sortKey, direction]);

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setDirection(direction === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      // Lower rates/prices first, higher score first, names A-Z.
      setDirection(key === 'score' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const header = (key: SortKey, label: string, align: 'left' | 'right' = 'right') => (
    <TableCell align={align} sortDirection={sortKey === key ? direction : false}>
      <TableSortLabel active={sortKey === key} direction={sortKey === key ? direction : 'asc'} onClick={() => sortBy(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Card>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ p: 2 }}>
        <TextField
          size="small"
          placeholder="Search make or model"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Body type"
          value={bodyType}
          onChange={(e) => {
            setBodyType(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All body types</MenuItem>
          {BODY_TYPE_CATEGORIES.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Data source"
          value={source}
          onChange={(e) => {
            setSource(e.target.value as SourceFilter);
            setPage(0);
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">Any source</MenuItem>
          <MenuItem value="adac">Has ADAC data</MenuItem>
          <MenuItem value="tuev">Has TÜV data</MenuItem>
          <MenuItem value="both">Has both</MenuItem>
        </TextField>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              {header('name', 'Model', 'left')}
              {header('score', 'Score', 'left')}
              {header('adac', 'ADAC breakdowns / 1,000')}
              {header('tuev', 'Best TÜV defect rate')}
              {header('price', 'Avg. price')}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((model) => (
              <ModelRow key={model.id} model={model} document={document} />
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No models match these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={rows.length}
        page={page}
        onPageChange={(_, value) => setPage(value)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </Card>
  );
}

function RankingList({ title, entries, format }: { title: string; entries: RateEntry[]; format: (value: number) => string }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No published figures.
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {entries.map((entry, index) => (
              <TableRow key={entry.model.id}>
                <TableCell sx={{ width: 32, color: 'text.secondary' }}>{index + 1}</TableCell>
                <TableCell>
                  <ModelName model={entry.model} variant="body2" />
                </TableCell>
                <TableCell align="right">{format(entry.value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function SourceRankings({
  title,
  description,
  tabs,
  render,
}: {
  title: string;
  description: string;
  tabs: { value: string; label: string }[];
  render: (tab: string) => { best: RateEntry[]; worst: RateEntry[]; format: (value: number) => string; note?: string };
}) {
  const [active, setActive] = useState(tabs[0]?.value);
  if (!tabs.length || !active) {
    return null;
  }
  const { best, worst: worstAll, format, note } = render(active);
  // With few published figures (e.g. only TÜV's top 10) the two lists would overlap - a model
  // already in the top list never also shows up in the bottom one.
  const bestIds = new Set(best.map((entry) => entry.model.id));
  const worst = worstAll.filter((entry) => !bestIds.has(entry.model.id)).slice(0, TOP_N);
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
      <Tabs
        value={active}
        onChange={(_, value) => setActive(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
      >
        {tabs.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>
      <CardContent>
        {note && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {note}
          </Typography>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
          <RankingList title="Top 10 – fewest problems" entries={best} format={format} />
          <RankingList title="Bottom 10 – most problems" entries={worst} format={format} />
        </Stack>
      </CardContent>
    </Card>
  );
}

function Methodology() {
  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          How the score works
        </Typography>
        <Stack spacing={1.5}>
          <Typography variant="body2">
            <strong>ADAC breakdown statistics (Pannenstatistik)</strong> – Germany's largest automobile club counts how
            often its roadside assistance is called out per 1,000 registered cars of each model, separately for every
            first-registration year. Published every spring.
          </Typography>
          <Typography variant="body2">
            <strong>TÜV-Report</strong> – the share of cars failing Germany's mandatory roadworthiness inspection (HU)
            with significant or dangerous defects, grouped by age (2–3 years, 4–5 years, …). Published every November.
          </Typography>
          <Typography variant="body2">
            Older cars always break down more, so every figure is compared with other cars of the same age: the median
            across all models for ADAC, the published class average for TÜV. Matching the average scores 50, half the
            average rate scores about 80, double scores about 20. The ADAC and TÜV scores are averaged, and models with
            very few data points are pulled towards 50.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Both reports cover the German market; the same models sold in Bulgaria share the same engineering, though
            climate, roads and maintenance habits differ. The data is re-checked every quarter and the price join is
            refreshed weekly.
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function ReliabilityDataPage() {
  const { data, isLoading, isError } = useReliability();

  const tuevTabs = useMemo(() => {
    if (!data) return [];
    const classes = data.sources.tuev?.ageClasses ?? [];
    return classes
      .filter((cls) => data.models.some((m) => m.tuev?.byAgeClass[cls]))
      .map((cls) => ({ value: cls, label: `${cls} years` }));
  }, [data]);

  const adacTabs = useMemo(() => (data ? adacRegistrationYears(data.models).map((year) => ({ value: year, label: year })) : []), [data]);

  return (
    <PageContainer>
      <Typography variant="h3" component="h1" gutterBottom>
        Reliability data
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 760, mb: 3 }}>
        Every model from the latest ADAC breakdown statistics and TÜV-Report, with the raw figures behind our
        reliability score. Expand a row for its year-by-year numbers.
      </Typography>

      {isLoading && <Skeleton variant="rounded" height={600} />}
      {(isError || (!isLoading && !data)) && <ReliabilityUnavailable />}

      {data && (
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
            <ReliabilitySources document={data} />
            <Button component={Link} to="/best-cars" variant="contained" color="secondary" startIcon={<EmojiEventsIcon />} sx={{ flexShrink: 0 }}>
              Best cars by category
            </Button>
          </Stack>

          <AllModelsTable document={data} />

          <SourceRankings
            title="TÜV-Report by age class"
            description="Share of cars with significant or dangerous defects at the roadworthiness inspection – lower is better."
            tabs={tuevTabs}
            render={(cls) => {
              const average = data.sources.tuev?.classAverages?.[cls];
              return {
                best: tuevRanking(data.models, cls, 'best'),
                worst: tuevRanking(data.models, cls, 'worst', Infinity),
                format: (v) => `${v.toFixed(1)}%`,
                note: average !== undefined ? `Average across all cars of this age: ${average.toFixed(1)}%.` : undefined,
              };
            }}
          />

          <SourceRankings
            title="ADAC breakdowns by registration year"
            description="Roadside-assistance call-outs per 1,000 registered cars – lower is better."
            tabs={adacTabs}
            render={(year) => ({
              best: adacRanking(data.models, year, 'best'),
              worst: adacRanking(data.models, year, 'worst', Infinity),
              format: (v) => v.toFixed(1),
            })}
          />

          <Methodology />
        </Stack>
      )}
    </PageContainer>
  );
}
